/**
 * Topic Tile — ריבוע נושא
 *
 * One square in the learning hub grid.
 *
 * Accessibility notes:
 *  - An unlocked tile is a real <Link> (renders <a href>), so it works with
 *    middle-click, Ctrl+click and screen-reader link lists.
 *  - The locked exam tile is a <button aria-disabled> rather than `disabled`:
 *    a disabled button leaves the tab order, which would hide the very
 *    explanation the student needs. It stays focusable and explains itself.
 *  - Status is conveyed in TEXT, never by colour alone.
 */

import { Link } from 'react-router-dom'
import { Check, Lock, PlayCircle } from 'lucide-react'
import { topicIcon, topicAccent } from './topicIcons'
import type { LearningTopicSummary } from '../../types'

interface TopicTileProps {
  topic: LearningTopicSummary
  /** URL root of the track this tile belongs to, e.g. '/pensia'. */
  basePath?: string
  /** Exam tiles are locked until every lesson is completed. */
  locked?: boolean
  lockHintId?: string
  /** Best exam score, shown on the exam tile. */
  bestScorePercent?: number | null
}

const STATUS_LABEL: Record<string, string> = {
  not_started: 'לא התחלת',
  lesson_viewed: 'בלמידה',
  completed: 'הושלם',
}

export default function TopicTile({
  topic,
  basePath = '/learn',
  locked = false,
  lockHintId,
  bestScorePercent,
}: TopicTileProps) {
  const Icon = topicIcon(topic.icon)
  const accent = topicAccent(topic.slug, topic.accent)
  const statusLabel = topic.isExam
    ? locked
      ? 'נעול'
      : typeof bestScorePercent === 'number' && bestScorePercent > 0
        ? `הציון שלך: ${bestScorePercent}%`
        : 'מוכן להתחלה'
    : STATUS_LABEL[topic.status] || 'לא התחלת'

  const StatusIcon = topic.status === 'completed' ? Check : locked ? Lock : PlayCircle

  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className={`rounded-xl p-3 ${accent.iconWrap}`}>
          <Icon size={26} aria-hidden="true" />
        </span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
            topic.status === 'completed'
              ? 'bg-green-100 text-green-800'
              : locked
                ? 'bg-gray-100 text-gray-700'
                : 'bg-white text-gray-700 border border-gray-200'
          }`}
        >
          <StatusIcon size={13} aria-hidden="true" />
          {statusLabel}
        </span>
      </div>

      <div className="mt-4">
        <h3 className="text-lg font-bold text-gray-900">{topic.title}</h3>
        {topic.subtitle && <p className="mt-1 text-sm text-gray-700">{topic.subtitle}</p>}
      </div>

      {topic.chatTurns > 0 && (
        <p className="mt-3 text-xs text-gray-600">שאלת {topic.chatTurns} שאלות בנושא הזה</p>
      )}
    </>
  )

  const shared = `flex h-full flex-col rounded-2xl border-2 p-5 text-right transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${accent.ring}`

  if (locked) {
    return (
      <button
        type="button"
        aria-disabled="true"
        aria-describedby={lockHintId}
        onClick={(e) => e.preventDefault()}
        className={`${shared} ${accent.tile} cursor-not-allowed opacity-75`}
      >
        {inner}
      </button>
    )
  }

  return (
    <Link
      to={topic.isExam ? `${basePath}/exam` : `${basePath}/topic/${topic.slug}`}
      className={`${shared} ${accent.tile}`}
      aria-label={`${topic.title} — ${statusLabel}`}
    >
      {inner}
    </Link>
  )
}
